import { Helmet } from "react-helmet-async";

const PrivacyPolicy = () => {
  return (
    <div
      style={{
        margin: "auto",
        padding: "1.5rem",
        backgroundColor: "white",
        boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
        borderRadius: "0.5rem",
      }}
    >
      <Helmet>
        <title>Privacy Policy | RD Rental Vehicle Rental Management Software</title>
        <meta
          name="description"
          content="Learn how RD Rental collects, uses, and protects your data when using our vehicle rental management software. Your privacy and data security matter to us."
        />
      </Helmet>

      <h1
        style={{
          fontSize: "2.25rem",
          fontWeight: "800",
          color: "#1F2937",
          marginBottom: "1.5rem",
          textAlign: "center",
        }}
      >
        Privacy Policy
      </h1>

      <p
        style={{
          color: "#4A5568",
          lineHeight: "1.75",
          marginBottom: "0.5rem",
        }}
      >
        <strong>Effective Date:</strong> August 10, 2026
      </p>

      <p
        style={{
          color: "#4A5568",
          lineHeight: "1.75",
          marginBottom: "1.5rem",
        }}
      >
        <strong>Last Updated:</strong> August 10, 2026
      </p>

      <p
        style={{
          color: "#4A5568",
          lineHeight: "1.75",
          marginBottom: "1.5rem",
        }}
      >
        At <strong>RDRental.com</strong> ("we," "our," or "us"), we are committed
        to protecting your privacy and ensuring transparency regarding how we
        collect, use, and share your personal information. This Privacy Policy
        applies to the RDRental.com mobile application, website, and related
        online rental services (collectively, the "Services").
      </p>

      <p
        style={{
          color: "#4A5568",
          lineHeight: "1.75",
          marginBottom: "1.5rem",
        }}
      >
        By accessing or using our Services, you agree to the collection and use
        of information in accordance with this Privacy Policy.
      </p>

      <h2
        style={{
          fontSize: "1.5rem",
          fontWeight: "600",
          color: "#2D3748",
          marginTop: "1.5rem",
          marginBottom: "1rem",
        }}
      >
        1. Information We Collect
      </h2>

      <p
        style={{
          color: "#4A5568",
          lineHeight: "1.75",
          marginBottom: "1rem",
        }}
      >
        We collect information directly from you, automatically when you use
        our app or website, and from third-party sources.
      </p>

      <h3
        style={{
          fontSize: "1.25rem",
          fontWeight: "600",
          color: "#374151",
          marginTop: "1rem",
          marginBottom: "0.75rem",
        }}
      >
        A. Information You Provide directly
      </h3>

      <ul
        style={{
          listStyleType: "disc",
          paddingLeft: "1.5rem",
          color: "#4A5568",
          marginBottom: "1.5rem",
          lineHeight: "1.75",
        }}
      >
        <li>
          <strong>Account & Profile Information:</strong> Name, email
          address, phone number, physical address, and account login
          credentials.
        </li>
        <li>
          <strong>Identity Verification Data:</strong> Driver’s license
          number, photo ID, date of birth, and identity verification photos
          required for rental eligibility.
        </li>
        <li>
          <strong>Payment & Transaction Details:</strong> Payment card
          details, billing address, booking history, deposit details, and
          invoice records (payment processing is handled by secure
          third-party payment processors).
        </li>
        <li>
          <strong>Communications:</strong> Messages, inquiries, customer
          support requests, and feedback submitted through the app or
          website.
        </li>
      </ul>

      <h3
        style={{
          fontSize: "1.25rem",
          fontWeight: "600",
          color: "#374151",
          marginTop: "1rem",
          marginBottom: "0.75rem",
        }}
      >
        B. Information Collected Automatically
      </h3>

      <ul
        style={{
          listStyleType: "disc",
          paddingLeft: "1.5rem",
          color: "#4A5568",
          marginBottom: "1.5rem",
          lineHeight: "1.75",
        }}
      >
        <li>
          <strong>Location Data:</strong> Precise or approximate location
          data from your mobile device (if enabled) to facilitate
          vehicle/equipment pickup, drop-off, geolocation tracking during
          rentals, and regional search results.
        </li>
        <li>
          <strong>Device & Usage Data:</strong> IP address, device ID,
          browser type, operating system, app crashes, performance data, and
          pages/screens viewed.
        </li>
        <li>
          <strong>Cookies & Tracking Technologies:</strong> Standard web
          cookies, local storage, and mobile analytical SDKs to remember user
          preferences and track platform interaction.
        </li>
      </ul>

      <h2
        style={{
          fontSize: "1.5rem",
          fontWeight: "600",
          color: "#2D3748",
          marginTop: "1.5rem",
          marginBottom: "1rem",
        }}
      >
        2. How We Use Your Information
      </h2>

      <p
        style={{
          color: "#4A5568",
          lineHeight: "1.75",
          marginBottom: "1rem",
        }}
      >
        We use the collected information for the following business purposes:
      </p>

      <ul
        style={{
          listStyleType: "disc",
          paddingLeft: "1.5rem",
          color: "#4A5568",
          marginBottom: "1.5rem",
          lineHeight: "1.75",
        }}
      >
        <li>
          <strong>Service Delivery & Fulfilling Rentals:</strong> Processing
          reservations, verifying driver eligibility, facilitating
          check-in/check-out, and processing payments.
        </li>
        <li>
          <strong>Account & Operations Management:</strong> Maintaining your
          profile, tracking active rentals, managing vehicle/equipment
          fleets, and providing customer support.
        </li>
        <li>
          <strong>Safety, Security & Verification:</strong> Preventing fraud,
          verifying identities, tracking rental assets, monitoring compliance
          with rental terms, and ensuring driver safety.
        </li>
        <li>
          <strong>Communications:</strong> Sending booking confirmations,
          rental reminders, updates, receipts, and administrative notices.
        </li>
        <li>
          <strong>Marketing & Analytics:</strong> Improving app performance,
          evaluating marketing effectiveness, and providing promotional
          offers (where permitted by law).
        </li>
        <li>
          <strong>Legal Compliance:</strong> Complying with statutory
          reporting requirements, tax obligations, and legal processes.
        </li>
      </ul>

      <h2
        style={{
          fontSize: "1.5rem",
          fontWeight: "600",
          color: "#2D3748",
          marginTop: "1.5rem",
          marginBottom: "1rem",
        }}
      >
        3. How We Share Your Information
      </h2>

      <p
        style={{
          color: "#4A5568",
          lineHeight: "1.75",
          marginBottom: "1rem",
        }}
      >
        We do not sell your personal data. We share information only under the
        following circumstances:
      </p>

      <ul
        style={{
          listStyleType: "disc",
          paddingLeft: "1.5rem",
          color: "#4A5568",
          marginBottom: "1.5rem",
          lineHeight: "1.75",
        }}
      >
        <li>
          <strong>Service Providers:</strong> Third-party vendors who assist
          with identity verification, payment processing, cloud hosting,
          customer support, and SMS/email delivery.
        </li>
        <li>
          <strong>Asset Tracking & Safety Providers:</strong> GPS and
          telematics providers integrated into fleet vehicles/equipment for
          security and location monitoring during active rental periods.
        </li>
        <li>
          <strong>Legal Requirements & Protection:</strong> Courts,
          regulatory authorities, law enforcement, or insurance partners when
          required by law or to enforce our rental agreements, protect
          property, or prevent physical or financial harm.
        </li>
        <li>
          <strong>Business Transfers:</strong> Parties involved in a
          corporate merger, sale of assets, consolidation, or acquisition
          involving RDRental.com.
        </li>
      </ul>

      <h2
        style={{
          fontSize: "1.5rem",
          fontWeight: "600",
          color: "#2D3748",
          marginTop: "1.5rem",
          marginBottom: "1rem",
        }}
      >
        4. Telematics & GPS Tracking
      </h2>

      <p
        style={{
          color: "#4A5568",
          lineHeight: "1.75",
          marginBottom: "1.5rem",
        }}
      >
        Vehicles or equipment rented through RDRental.com may be equipped with
        GPS tracking, telematics, and diagnostics systems. These systems
        collect data such as real-time location, mileage, speed, vehicle health
        metrics, and impact/accident detection. This data is used for fleet
        maintenance, safety, vehicle recovery, and enforcing rental boundary
        limits.
      </p>

      <h2
        style={{
          fontSize: "1.5rem",
          fontWeight: "600",
          color: "#2D3748",
          marginTop: "1.5rem",
          marginBottom: "1rem",
        }}
      >
        5. Data Security and Retention
      </h2>

      <p
        style={{
          color: "#4A5568",
          lineHeight: "1.75",
          marginBottom: "1rem",
        }}
      >
        We maintain appropriate administrative, technical, and physical
        safeguards designed to protect personal information against
        unauthorized access, loss, misuse, or disclosure.
      </p>

      <p
        style={{
          color: "#4A5568",
          lineHeight: "1.75",
          marginBottom: "1.5rem",
        }}
      >
        We retain your personal data for as long as necessary to fulfill the
        purposes outlined in this policy, maintain active customer accounts,
        resolve disputes, and satisfy statutory record-keeping obligations
        (such as tax and financial auditing standards).
      </p>

      <h2
        style={{
          fontSize: "1.5rem",
          fontWeight: "600",
          color: "#2D3748",
          marginTop: "1.5rem",
          marginBottom: "1rem",
        }}
      >
        6. Your Rights and Privacy Choices
      </h2>

      <p
        style={{
          color: "#4A5568",
          lineHeight: "1.75",
          marginBottom: "1rem",
        }}
      >
        Depending on your jurisdiction, you may have the following rights
        regarding your personal information:
      </p>

      <ul
        style={{
          listStyleType: "disc",
          paddingLeft: "1.5rem",
          color: "#4A5568",
          marginBottom: "1.5rem",
          lineHeight: "1.75",
        }}
      >
        <li>
          <strong>Access & Portability:</strong> Request a copy of the
          personal data we hold about you.
        </li>
        <li>
          <strong>Correction:</strong> Update or correct inaccurate account
          information directly through your app settings or by contacting us.
        </li>
        <li>
          <strong>Deletion:</strong> Request the deletion of your account and
          associated personal data, subject to legal and contractual
          retention requirements (such as active rental agreements or unpaid
          balances).
        </li>
        <li>
          <strong>Location Access:</strong> Disable location permissions
          through your device settings at any time (note: disabling location
          services may affect core functionality of the app).
        </li>
        <li>
          <strong>Marketing Opt-Out:</strong> Unsubscribe from promotional
          communications using the link provided in emails or through
          notification preferences in the app.
        </li>
      </ul>

      <h2
        style={{
          fontSize: "1.5rem",
          fontWeight: "600",
          color: "#2D3748",
          marginTop: "1.5rem",
          marginBottom: "1rem",
        }}
      >
        7. Children's Privacy
      </h2>

      <p
        style={{
          color: "#4A5568",
          lineHeight: "1.75",
          marginBottom: "1.5rem",
        }}
      >
        Our Services are intended exclusively for adults and legally eligible
        drivers/renters. We do not knowingly collect or solicit personal
        information from children under the age of 13 (or 16 where required by
        law). If we learn that we have inadvertently collected data from a
        minor, we will promptly delete it.
      </p>

      <h2
        style={{
          fontSize: "1.5rem",
          fontWeight: "600",
          color: "#2D3748",
          marginTop: "1.5rem",
          marginBottom: "1rem",
        }}
      >
        8. Third-Party Links & Services
      </h2>

      <p
        style={{
          color: "#4A5568",
          lineHeight: "1.75",
          marginBottom: "1.5rem",
        }}
      >
        The RDRental.com app and website may contain links to third-party
        services, mapped APIs, or external payment portals. We are not
        responsible for the privacy practices or content of these third-party
        platforms.
      </p>

      <h2
        style={{
          fontSize: "1.5rem",
          fontWeight: "600",
          color: "#2D3748",
          marginTop: "1.5rem",
          marginBottom: "1rem",
        }}
      >
        9. Updates to This Privacy Policy
      </h2>

      <p
        style={{
          color: "#4A5568",
          lineHeight: "1.75",
          marginBottom: "1.5rem",
        }}
      >
        We may update this Privacy Policy periodically to reflect changes in
        our operational practices, technical capabilities, or legal
        obligations. We will notify you of material changes by posting the
        updated policy on our platform with a revised "Last Updated" date or
        through direct in-app communication.
      </p>

      <h2
        style={{
          fontSize: "1.5rem",
          fontWeight: "600",
          color: "#2D3748",
          marginTop: "1.5rem",
          marginBottom: "1rem",
        }}
      >
        10. Contact Us
      </h2>

      <p
        style={{
          color: "#4A5568",
          lineHeight: "1.75",
          marginBottom: "1rem",
        }}
      >
        If you have any questions, concerns, or privacy requests regarding this
        Privacy Policy, please contact us at:
      </p>

      <p
        style={{
          color: "#4A5568",
          lineHeight: "1.75",
          marginBottom: "1.5rem",
        }}
      >
        <strong>Website:</strong>{" "}
        <a
          href="https://rdrental.com/contact-us"
          target="_blank"
          rel="noopener noreferrer"
          style={{
            color: "#2563EB",
            textDecoration: "underline",
          }}
        >
          https://rdrental.com/contact-us
        </a>
      </p>
    </div>
  );
};

export default PrivacyPolicy;